import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { formatNumber } from '../../utils/formatters';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './ImportOrdiniGoPage.api';

type StatoImport = 'CARICATO' | 'IN_ELABORAZIONE' | 'ELABORATO' | 'ERRORE';

interface Insegna { id: number; codice: string; nome: string }

interface CaricamentoRow {
  id: number;
  societaId: number;
  etichetta: string;
  note: string | null;
  stato: StatoImport;
  utenteName: string;
  totaleFile: number;
  ordiniCreati: number;
  righeCreate: number;
  righeNonRisolte: number;
  createdAt: string;
  insegna: Insegna;
  _count: { file: number };
  // totali calcolati dal backend: imponibile dai file e imponibile registrato in GO
  totaleDaFile: number | null;
  totaleInGo: number | null;
  // quanti ordini dovrebbero essere in GO (gli scartati non contano) e quanti
  // ci sono davvero: i due totali sono confrontabili solo se coincidono
  ordiniDaInviare: number;
  ordiniInviati: number;
}

interface Scartato { nomeFile: string; motivo: string }

const STATO_LABEL: Record<StatoImport, { label: string; variant: string }> = {
  CARICATO:        { label: 'Da elaborare', variant: 'info' },
  IN_ELABORAZIONE: { label: 'In elaborazione', variant: 'warning' },
  ELABORATO:       { label: 'Elaborato', variant: 'success' },
  ERRORE:          { label: 'Errore', variant: 'danger' },
};

const ESTENSIONI_AMMESSE = '.xml,.txt,.csv,.xls,.xlsx,.pdf';

/**
 * Il totale registrato in GO, con il segnale di quanto si discosta da quello
 * dichiarato dai file.
 *
 * Due totali diversi hanno due cause che non vanno confuse:
 *
 *   **Mancano ordini in GO** (ambra) — una parte del caricamento non è stata
 *   ancora inviata. La differenza è aritmetica e attesa: non c'è niente da
 *   correggere, c'è da finire di inviare.
 *
 *   **Ci sono tutti e i conti non tornano** (rosso) — GO ha registrato un
 *   imponibile diverso da quello che il cliente dichiarava. Qui c'è qualcosa da
 *   guardare: prezzi, righe scartate, articoli agganciati male.
 *
 * Il denominatore è `ordiniDaInviare`, non `ordiniCreati`: gli ordini scartati
 * in GO non ci vanno e non pesano nemmeno sul totale da file, quindi un
 * caricamento con scarti è "completo" pur avendo meno ordini di quanti ne sono
 * stati creati.
 */
function TotaleInGo({ riga }: { riga: CaricamentoRow }) {
  if (riga.totaleInGo === null || riga.totaleInGo === undefined) {
    return <span className="bop-text-muted">—</span>;
  }

  const attesi = riga.ordiniDaInviare ?? riga.ordiniCreati;
  const mancanti = Math.max(0, attesi - riga.ordiniInviati);
  const scostamento = riga.totaleDaFile === null || riga.totaleDaFile === undefined
    ? null
    : riga.totaleInGo - riga.totaleDaFile;
  // Sotto il centesimo è arrotondamento, non uno scostamento.
  const diverso = scostamento !== null && Math.abs(scostamento) > 0.005;
  const delta = scostamento === null
    ? ''
    : `${scostamento > 0 ? '+' : '−'}${formatNumber(Math.abs(scostamento))} €`;

  const uno = mancanti === 1;

  let segnale = null;
  if (diverso && mancanti > 0) {
    segnale = (
      <span
        className="bop-badge bop-badge-warning"
        title={`In GO ci sono ${riga.ordiniInviati} ordini su ${attesi}: ${uno ? "manca l'ordine" : `mancano i ${mancanti} ordini`} non ancora ${uno ? 'inviato' : 'inviati'}, e la differenza di ${delta} rispetto al totale dai file è attesa.`}
      >
        {delta} · {riga.ordiniInviati}/{attesi}
      </span>
    );
  } else if (diverso) {
    segnale = (
      <span
        className="bop-badge bop-badge-danger"
        title={`Tutti i ${attesi} ordini sono in GO, ma GO ha registrato ${formatNumber(riga.totaleInGo)} € contro i ${formatNumber(riga.totaleDaFile as number)} € dichiarati dai file: ${delta}. Da controllare.`}
      >
        ≠ file {delta}
      </span>
    );
  } else if (mancanti > 0) {
    // Totali uguali ma ordini mancanti: succede quando i non inviati valgono
    // zero. Si dice comunque che il caricamento non è finito, senza allarmare.
    segnale = (
      <span
        className="bop-badge bop-badge-neutral"
        title={`In GO ci sono ${riga.ordiniInviati} ordini su ${attesi}`}
      >
        {riga.ordiniInviati}/{attesi} in GO
      </span>
    );
  }

  return (
    <span className="bop-go-totale">
      <span className={diverso ? 'bop-go-totale__cifra--diverso' : undefined}>
        {formatNumber(riga.totaleInGo)}
      </span>
      {segnale}
    </span>
  );
}

function fmtDataOra(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT');
}

export default function ImportOrdiniGoPage() {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [data, setData] = useState<CaricamentoRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modale nuovo caricamento
  const [modalOpen, setModalOpen] = useState(false);
  const [insegne, setInsegne] = useState<Insegna[]>([]);
  const [formInsegnaId, setFormInsegnaId] = useState('');
  const [formEtichetta, setFormEtichetta] = useState('');
  const [formNote, setFormNote] = useState('');
  const [formFile, setFormFile] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [scartati, setScartati] = useState<Scartato[]>([]);
  const [aiutoAperto, setAiutoAperto] = useState(false);

  // Scansione della cartella Google Drive su richiesta
  const [scansionando, setScansionando] = useState(false);
  const [esitoScansione, setEsitoScansione] = useState<string>('');
  const [erroreScansione, setErroreScansione] = useState<string>('');

  const canEdit = can('bop_go.import_ordini_smartix', 'edit');

  async function scansionaDrive() {
    if (!societaId) { setErroreScansione('Seleziona una società per avviare la scansione'); return; }
    if (!window.confirm('Leggere adesso la cartella Google Drive? I file trovati verranno caricati, elaborati e, se completi, inviati a GO.')) return;
    setScansionando(true);
    setEsitoScansione('');
    setErroreScansione('');
    try {
      // @api POST /api/bop-go/import-ordini/drive/scansiona {"societaId":1} -> {"success":true,"data":{"fileTrovati":3,"lotti":[{"insegna":"Bricocenter","caricamentoId":12,"esito":"inviati a GO 20 ordini su 20"}],"fileNonRiconosciuti":[],"fileDuplicati":[],"errori":[],"durataMs":45000}}
      const res = await apiFetch<{ success: boolean; data: { fileTrovati: number; lotti: { insegna: string; caricamentoId: number | null; esito?: string }[]; fileNonRiconosciuti: { nome: string }[]; errori: string[]; durataMs: number } }>(
        '/api/bop-go/import-ordini/drive/scansiona',
        { method: 'POST', body: JSON.stringify({ societaId: Number(societaId) }) }
      );
      const d = res.data;
      const parti = [`${d.fileTrovati} file trovati`, `${d.lotti.length} lotti`];
      for (const l of d.lotti) parti.push(`${l.insegna}${l.caricamentoId ? ` #${l.caricamentoId}` : ''}: ${l.esito || '—'}`);
      if (d.fileNonRiconosciuti.length) parti.push(`${d.fileNonRiconosciuti.length} file non riconosciuti`);
      if (d.errori.length) parti.push(`${d.errori.length} errori`);
      setEsitoScansione(parti.join(' · '));
      await fetchData();
    } catch (err: any) {
      setErroreScansione(err.message || 'Scansione fallita');
    } finally {
      setScansionando(false);
    }
  }

  useEffect(() => {
    if (!pronta) return;
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronta, societaId]);

  async function fetchData() {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (societaId) params.append('societaId', societaId);
      // @api GET /api/bop-go/import-ordini?societaId=&insegnaId=&stato= -> {"success":true,"data":[{"id":1,"societaId":1,"etichetta":"Bricocenter 20/08","note":null,"stato":"CARICATO","utenteName":"...","totaleFile":3,"ordiniCreati":0,"righeCreate":0,"righeNonRisolte":0,"createdAt":"...","insegna":{"id":1,"codice":"BC","nome":"Bricocenter"},"_count":{"file":3},"totaleDaFile":3557.06,"totaleInGo":3557.06,"ordiniInviati":20}]}
      const res = await apiFetch<{ success: boolean; data: CaricamentoRow[] }>(`/api/bop-go/import-ordini?${params.toString()}`);
      setData(res.data || []);
    } catch (err) {
      console.error('[ImportOrdiniGoPage] fetch error', err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function apriModale() {
    setFormInsegnaId('');
    setFormEtichetta('');
    setFormNote('');
    setFormFile([]);
    setModalError('');
    setScartati([]);
    setModalOpen(true);

    if (!societaId) {
      setModalError('Seleziona prima una società');
      return;
    }
    try {
      // @api GET /api/bop-go/import-ordini/insegne?societaId=1 -> {"success":true,"data":[{"id":1,"codice":"BC","nome":"Bricocenter"}]}
      const res = await apiFetch<{ success: boolean; data: Insegna[] }>(`/api/bop-go/import-ordini/insegne?societaId=${societaId}`);
      setInsegne(res.data || []);
    } catch (err: any) {
      setModalError(err.message || 'Errore nel caricamento delle insegne');
    }
  }

  function chiudiModale() {
    if (saving) return;
    setModalOpen(false);
    setScartati([]);
  }

  async function handleSalva() {
    if (!societaId) { setModalError('Seleziona una società'); return; }
    if (!formInsegnaId) { setModalError("Seleziona l'insegna"); return; }
    if (!formEtichetta.trim()) { setModalError("Inserisci un'etichetta per il caricamento"); return; }
    if (formFile.length === 0) { setModalError('Seleziona almeno un file'); return; }

    setSaving(true);
    setModalError('');
    setScartati([]);
    try {
      const fd = new FormData();
      fd.append('societaId', societaId);
      fd.append('insegnaClienteId', formInsegnaId);
      fd.append('etichetta', formEtichetta.trim());
      if (formNote.trim()) fd.append('note', formNote.trim());
      formFile.forEach(f => fd.append('file', f));

      // @api POST /api/bop-go/import-ordini -> {"success":true,"data":{"id":1,"fileCaricati":3,"scartati":[{"nomeFile":"a.xml","motivo":"già caricato..."}],"avvisi":[]}}
      const res = await apiFetch<{ success: boolean; data: { id: number; fileCaricati: number; scartati: Scartato[]; avvisi: string[] } }>(
        '/api/bop-go/import-ordini',
        { method: 'POST', body: fd }
      );
      setModalOpen(false);
      navigate(`/bop-go/import-ordini-smartix/${res.data.id}`);
    } catch (err: any) {
      // Il backend risponde 409 con l'elenco dei file scartati quando erano
      // già stati caricati tutti: qui vale la pena mostrarli uno per uno.
      const scartatiErr = err?.payload?.data?.scartati as Scartato[] | undefined;
      if (scartatiErr?.length) setScartati(scartatiErr);
      setModalError(err.message || 'Errore nel caricamento');
    } finally {
      setSaving(false);
    }
  }

  const columns = useMemo<BopExcelColumn<CaricamentoRow>[]>(() => [
    {
      key: 'createdAt',
      header: 'Caricato il',
      accessor: (r) => r.createdAt,
      filter: { type: 'dateRange' },
      width: '150px',
      render: (r) => fmtDataOra(r.createdAt),
    },
    {
      key: 'etichetta',
      header: 'Etichetta',
      accessor: (r) => r.etichetta,
      filter: { type: 'text' },
      render: (r) => <strong>{r.etichetta}</strong>,
    },
    {
      key: 'insegna',
      header: 'Insegna',
      accessor: (r) => r.insegna?.nome || '',
      filter: { type: 'text' },
      render: (r) => r.insegna?.nome ?? '—',
    },
    {
      key: 'file',
      header: 'File',
      accessor: (r) => r._count?.file ?? 0,
      filter: { type: 'number' },
      width: '80px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => r._count?.file ?? 0,
    },
    {
      key: 'ordini',
      header: 'Ordini',
      accessor: (r) => r.ordiniCreati,
      filter: { type: 'number' },
      width: '80px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => r.stato === 'CARICATO' ? <span className="bop-text-muted">—</span> : r.ordiniCreati,
    },
    {
      key: 'righe',
      header: 'Righe',
      accessor: (r) => r.righeCreate,
      filter: { type: 'number' },
      width: '80px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => r.stato === 'CARICATO' ? <span className="bop-text-muted">—</span> : r.righeCreate,
    },
    {
      key: 'totaleDaFile',
      header: 'Tot. € da file',
      accessor: (r) => r.totaleDaFile ?? 0,
      filter: { type: 'number' },
      width: '120px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (r) => r.totaleDaFile === null || r.totaleDaFile === undefined
        ? <span className="bop-text-muted">—</span>
        : formatNumber(r.totaleDaFile),
    },
    {
      key: 'totaleInGo',
      header: 'Tot. € da GO',
      accessor: (r) => r.totaleInGo ?? 0,
      filter: { type: 'number' },
      width: '150px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      // Due totali diversi possono avere due cause opposte, e vanno distinte a
      // colpo d'occhio: mancano ordini in GO (differenza attesa, ambra) oppure
      // ci sono tutti e i conti non tornano lo stesso (da guardare, rosso).
      render: (r) => <TotaleInGo riga={r} />,
    },
    {
      key: 'utente',
      header: 'Utente',
      accessor: (r) => r.utenteName,
      filter: { type: 'text' },
      render: (r) => r.utenteName,
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (r) => STATO_LABEL[r.stato].label,
      filter: {
        type: 'enum',
        options: Object.values(STATO_LABEL).map(s => ({ value: s.label, label: s.label })),
      },
      width: '140px',
      render: (r) => {
        const s = STATO_LABEL[r.stato];
        return <span className={`bop-badge bop-badge-${s.variant}`}>{s.label}</span>;
      },
    },
  ], []);

  return (
    <Layout title="Import ordini Smartix in GO">
      <div className="bop-page-header">
        <h1 className="bop-page-title">Import ordini Smartix in GO</h1>
        {canEdit && (
          <div className="bop-flex">
            <button className="bop-btn bop-btn-secondary" onClick={scansionaDrive} disabled={scansionando}
              title="Legge subito la cartella Google Drive configurata in Match campi BOP-GO">
              {scansionando ? 'Scansione in corso…' : 'Scansiona Drive ora'}
            </button>
            <button className="bop-btn bop-btn-primary" onClick={apriModale}>
              + Nuovo caricamento
            </button>
          </div>
        )}
      </div>

      {erroreScansione && <p className="bop-text-danger">{erroreScansione}</p>}
      {esitoScansione && <p className="bop-text-muted">{esitoScansione}</p>}

      <p className="bop-text-muted">
        Ogni caricamento raccoglie i file di ordini ricevuti da una insegna. Formati accettati:
        XML, TXT e CSV (testate e righe), XLS di riepilogo (riscontro sui totali) e PDF scansionati, letti con OCR.
        Un file già caricato viene riconosciuto dal contenuto e scartato, anche se rinominato.
      </p>

      {!isUnica && (
        <div className="bop-filter-bar">
          <select className="bop-select" value={societaId} onChange={(e) => setSocietaId(e.target.value)}>
            <option value="">Tutte le società</option>
            {listaSocieta.map((s) => (
              <option key={s.id} value={s.id}>{s.nome}</option>
            ))}
          </select>
        </div>
      )}

      <BopExcelTable<CaricamentoRow>
        data={data}
        loading={isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/bop-go/import-ordini-smartix/${r.id}`)}
        initialSort={{ key: 'createdAt', dir: 'desc' }}
        storageKey="bop-go-import-ordini"
        resizableColumns
        emptyState={<div className="bop-empty-state">Nessun caricamento presente</div>}
        columns={columns}
      />

      {modalOpen && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--lg">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Nuovo caricamento</h2>
              <button className="bop-modal__close" onClick={chiudiModale} aria-label="Chiudi" disabled={saving}>✕</button>
            </div>
            <div className="bop-modal__body">
              <div className="bop-card">
                <div className="bop-card__header">
                  <h3 className="bop-card__title">Un caricamento = un lotto di ordini</h3>
                  <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => setAiutoAperto(!aiutoAperto)}>
                    {aiutoAperto ? 'Nascondi' : 'Come funziona'}
                  </button>
                </div>
                <div className="bop-card__body">
                  <p className="bop-text-muted">
                    Carica insieme <strong>tutti i tracciati dello stesso invio</strong> — XML, TXT, CSV e il
                    riepilogo XLS: si completano a vicenda e non generano doppioni.
                    <strong> Non mescolare invii diversi</strong> nello stesso caricamento: il riepilogo di
                    quadratura copre un solo lotto, e i conteggi diventano illeggibili.
                  </p>

                  {aiutoAperto && (
                    <>
                      <h4 className="bop-card__title">Perché caricarli tutti insieme</h4>
                      <p className="bop-text-muted">
                        Gli stessi ordini arrivano in formati diversi che non contengono le stesse informazioni:
                        l'XML porta l'identificativo del portale, il TXT e il CSV portano indirizzo del negozio,
                        deposito e totale. BOP li riconosce come lo stesso ordine e unisce i campi, senza mai
                        sovrascrivere un dato già letto. L'XLS non contiene righe: serve solo a controllare che
                        il numero di ordini estratti torni con quello dichiarato dal portale.
                      </p>

                      <h4 className="bop-card__title">Cosa serve perché le righe si aggancino da sole</h4>
                      <ul className="bop-text-muted">
                        <li>
                          <strong>Articoli con marca Smartix.</strong> Il match cerca solo fra quelli: senza marca
                          assegnata, un articolo non viene trovato nemmeno se il codice coincide.
                        </li>
                        <li>
                          <strong>Codice o EAN in comune con la catena.</strong> Se l'articolo ha il codice che la
                          catena ci rimanda, o l'EAN corrispondente, l'aggancio è automatico e il codice della
                          catena viene <em>memorizzato da solo</em> sull'insegna: dal caricamento successivo
                          quel codice basta da solo.
                        </li>
                        <li>
                          <strong>Codici articolo dell'insegna</strong> (Archivi → Insegne → Codici articolo):
                          da compilare a mano solo per gli articoli che non hanno né codice né EAN in comune.
                        </li>
                        <li>
                          <strong>Codici articolo del cliente</strong> (scheda articolo → Codici cliente): servono
                          solo quando una singola ragione sociale della catena usa una codifica diversa da quella
                          dell'insegna.
                        </li>
                      </ul>

                      <h4 className="bop-card__title">Cosa serve perché il negozio venga riconosciuto</h4>
                      <p className="bop-text-muted">
                        I punti vendita che emettono gli ordini vanno come <strong>filiali</strong> del cliente
                        legato all'insegna. Non è obbligatorio prepararle prima: in verifica si creano al volo con
                        nome, indirizzo, CAP e città letti dal file. Un negozio non riconosciuto non blocca
                        l'import, ma va risolto prima di poter confermare l'ordine.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="bop-form-group">
                <label className="bop-label" htmlFor="import-insegna">Insegna *</label>
                <select
                  id="import-insegna"
                  className="bop-select"
                  value={formInsegnaId}
                  onChange={(e) => { setFormInsegnaId(e.target.value); setModalError(''); }}
                  disabled={saving}
                >
                  <option value="">— seleziona —</option>
                  {insegne.map(i => (
                    <option key={i.id} value={i.id}>{i.nome}</option>
                  ))}
                </select>
                <p className="bop-text-muted">
                  L'insegna determina il tracciato con cui verranno letti i file.
                </p>
              </div>

              <div className="bop-form-group">
                <label className="bop-label" htmlFor="import-etichetta">Etichetta *</label>
                <input
                  id="import-etichetta"
                  type="text"
                  className="bop-input"
                  maxLength={60}
                  value={formEtichetta}
                  onChange={(e) => { setFormEtichetta(e.target.value); setModalError(''); }}
                  placeholder="es. Bricocenter — invio del 20/08"
                  disabled={saving}
                />
              </div>

              <div className="bop-form-group">
                <label className="bop-label" htmlFor="import-note">Note</label>
                <input
                  id="import-note"
                  type="text"
                  className="bop-input"
                  maxLength={200}
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="bop-form-group">
                <label className="bop-label" htmlFor="import-file">File *</label>
                <input
                  id="import-file"
                  type="file"
                  className="bop-input"
                  multiple
                  accept={ESTENSIONI_AMMESSE}
                  onChange={(e) => { setFormFile(Array.from(e.target.files || [])); setModalError(''); }}
                  disabled={saving}
                />
                {formFile.length > 0 && (
                  <p className="bop-text-muted">{formFile.length} file selezionati</p>
                )}
              </div>

              {scartati.length > 0 && (
                <div className="bop-card">
                  <p className="bop-text-muted">File scartati perché già presenti:</p>
                  <ul>
                    {scartati.map((s, i) => (
                      <li key={i}><strong>{s.nomeFile}</strong> — {s.motivo}</li>
                    ))}
                  </ul>
                </div>
              )}

              {modalError && <p className="bop-text-danger">{modalError}</p>}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={chiudiModale} disabled={saving}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={handleSalva} disabled={saving}>
                {saving ? 'Caricamento...' : 'Carica'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
